import { useCallback, useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import PortalHeader from '../components/PortalHeader';
import { messagesAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { btnPrimary, inputCls, panelCls } from '../components/ui';
import { nyTime } from '../lib/nyTime';

export default function MessagesPage() {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);

  const load = useCallback(() => {
    messagesAPI.myThread()
      .then(({ data }) => setMessages(data.messages))
      .catch((err) => toast.error(err.response?.data?.detail || t('Could not load messages')));
  }, []);
  useEffect(load, [load]);
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await messagesAPI.send(text.trim());
      setText('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Send failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <PortalHeader title={t('Messages')} />
      <div className={`${panelCls} flex h-[60vh] flex-col`}>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {messages === null ? (
            <p className="text-sm text-mms-muted">{t('Loading…')}</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-mms-muted">{t('No messages yet — ask the program team anything.')}</p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                m.from === 'member' ? 'ml-auto bg-mms-accent text-mms-accent-ink' : 'bg-mms-raised'}`}>
                {m.body}
                <div className={`mt-1 text-[10px] ${m.from === 'member' ? 'text-mms-accent-ink/70' : 'text-mms-faint'}`}>
                  {m.from === 'staff' ? `${t('Team')} · ` : ''}{nyTime(m.at)}
                </div>
              </div>
            ))
          )}
          <div ref={bottom} />
        </div>
        <form onSubmit={send} className="flex gap-2 border-t border-mms-border p-3">
          <input className={inputCls} value={text} placeholder={t('Write a message…')}
            onChange={(e) => setText(e.target.value)} />
          <button type="submit" disabled={busy || !text.trim()} className={btnPrimary}>
            <Send size={14} /> {t('Send')}
          </button>
        </form>
      </div>
    </div>
  );
}
